/**
 * Client-side filter/sort/paginate for artist metaobject pages.
 *
 * Shopify Liquid does not expose query params on metaobject templates, so
 * `sections/artist-collection.liquid` renders every product once and ships
 * a parallel JSON metadata array. This script reads that array plus the
 * current URL params and reorders / hides cards in place, then renders its
 * own pagination. Triggered on load, on `collection:apply-client` events
 * fired by `collection-filter.js`, and on popstate.
 */
(function () {
  'use strict';

  var RESULTS_ID = (window.BB && window.BB.RESULTS_ID) || 'collection-results';

  function getRoot() {
    var r = document.getElementById(RESULTS_ID);
    if (!r || r.getAttribute('data-collection-mode') !== 'client') return null;
    return r;
  }

  function readData(root) {
    var script = root.querySelector('[data-artist-product-data]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent || '[]');
    } catch (e) {
      return null;
    }
  }

  function getCards(root) {
    var grid = root.querySelector('.product-grid');
    if (!grid) return [];
    return Array.prototype.slice.call(grid.children).filter(function (el) {
      return el.tagName === 'ARTICLE';
    });
  }

  function parseParams(url) {
    var q = url.searchParams;
    var gte = parseFloat(q.get('filter.v.price.gte'));
    var lte = parseFloat(q.get('filter.v.price.lte'));
    var page = parseInt(q.get('page') || '1', 10);
    return {
      sort: q.get('sort_by') || 'manual',
      avail: q.get('filter.v.availability') === '1',
      gte: isNaN(gte) ? null : Math.round(gte * 100),
      lte: isNaN(lte) ? null : Math.round(lte * 100),
      page: page > 0 ? page : 1,
    };
  }

  function makeSorter(sort) {
    var primary;
    switch (sort) {
      case 'title-ascending':
        primary = function (a, b) { return a.data.t.localeCompare(b.data.t); }; break;
      case 'title-descending':
        primary = function (a, b) { return b.data.t.localeCompare(a.data.t); }; break;
      case 'price-ascending':
        primary = function (a, b) { return a.data.p - b.data.p; }; break;
      case 'price-descending':
        primary = function (a, b) { return b.data.p - a.data.p; }; break;
      case 'created-ascending':
        primary = function (a, b) { return a.data.c - b.data.c; }; break;
      case 'created-descending':
        primary = function (a, b) { return b.data.c - a.data.c; }; break;
      default:
        // "manual" / unknown: restore original JSON order. Must not be a no-op,
        // otherwise current (already-sorted) DOM order leaks through.
        primary = function () { return 0; };
    }
    // Tiebreak on original index so equal-key items always land in the same
    // relative order regardless of current DOM state or engine sort stability.
    return function (a, b) {
      var r = primary(a, b);
      return r !== 0 ? r : a.order - b.order;
    };
  }

  function renderPagination(container, url, page, pages) {
    if (!container) return;
    while (container.firstChild) container.removeChild(container.firstChild);
    if (pages <= 1) return;
    var base = container.getAttribute('data-base-url') || url.pathname;
    var qsBase = new URLSearchParams(url.search);

    function pageUrl(p) {
      var u = new URLSearchParams(qsBase);
      if (p <= 1) u.delete('page');
      else u.set('page', String(p));
      var s = u.toString();
      return base + (s ? '?' + s : '');
    }

    function makeLink(p, label, extraClass) {
      var a = document.createElement('a');
      a.href = pageUrl(p);
      a.className = extraClass || 'text-mocha-800 hover:text-mocha-950 px-2 underline decoration-mocha-400 underline-offset-2';
      a.textContent = label;
      return a;
    }

    var nav = document.createElement('nav');
    nav.className = 'pagination';
    nav.setAttribute('aria-label', 'Pagination');

    if (page > 1) {
      var prev = makeLink(page - 1, '«', 'text-mocha-900 font-semibold underline decoration-mocha-500 underline-offset-2');
      prev.setAttribute('aria-label', 'Previous page');
      nav.appendChild(prev);
    }

    for (var p = 1; p <= pages; p++) {
      if (p === page) {
        var span = document.createElement('span');
        span.className = 'page current font-bold text-mocha-950 px-2';
        span.textContent = p;
        span.setAttribute('aria-current', 'page');
        nav.appendChild(span);
      } else {
        nav.appendChild(makeLink(p, p));
      }
    }

    if (page < pages) {
      var next = makeLink(page + 1, '»', 'text-mocha-900 font-semibold underline decoration-mocha-500 underline-offset-2');
      next.setAttribute('aria-label', 'Next page');
      nav.appendChild(next);
    }

    container.appendChild(nav);
  }

  function bindData(root) {
    if (root._artistBound) return true;
    var data = readData(root);
    var cards = getCards(root);
    if (!data || cards.length !== data.length) return false;
    cards.forEach(function (c, i) {
      c._artistData = data[i];
      c._artistOrder = i;
    });
    root._artistBound = true;
    return true;
  }

  function apply(root, url) {
    if (root._applying) return;
    root._applying = true;
    if (!bindData(root)) { root._applying = false; return; }
    var cards = getCards(root);

    var params = parseParams(url);
    var perPage = parseInt(root.getAttribute('data-per-page') || '40', 10) || 40;

    var indexed = cards.map(function (c) {
      return { card: c, data: c._artistData, order: c._artistOrder };
    });

    var filtered = indexed.filter(function (x) {
      if (params.avail && !x.data.a) return false;
      if (params.gte != null && x.data.p < params.gte) return false;
      if (params.lte != null && x.data.p > params.lte) return false;
      return true;
    });

    var sorter = makeSorter(params.sort);
    if (sorter) filtered.sort(sorter);

    var total = filtered.length;
    var pages = Math.max(1, Math.ceil(total / perPage));
    var page = Math.min(params.page, pages);
    var off = (page - 1) * perPage;
    var visible = filtered.slice(off, off + perPage);

    cards.forEach(function (c) { c.style.display = 'none'; });
    var grid = root.querySelector('.product-grid');
    if (grid) {
      visible.forEach(function (x) {
        x.card.style.display = '';
        grid.appendChild(x.card);
      });
    }

    renderPagination(
      root.querySelector('[data-artist-pagination]'),
      url,
      page,
      pages
    );

    var noMatch = root.querySelector('[data-artist-no-match]');
    if (noMatch) {
      noMatch.classList.toggle('hidden', total !== 0);
    }
    if (grid) grid.style.display = total === 0 ? 'none' : '';

    root._applying = false;
    root.dispatchEvent(new CustomEvent('collection:updated', { bubbles: true }));
  }

  function init() {
    var root = getRoot();
    if (!root) return;
    apply(root, new URL(window.location.href));
    document.addEventListener('collection:apply-client', function (ev) {
      var r = getRoot();
      if (!r) return;
      var u = (ev.detail && ev.detail.url) ? ev.detail.url : new URL(window.location.href);
      apply(r, u);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
